import { create } from "zustand";
import type { CanvasObject, CanvasOp, TransformObjectOp } from "@refinement-canvas/domain";
import { DEFAULT_CANVAS_TITLE } from "../../../lib/canvas-title";

// キャンバスのドキュメント状態（メタ情報とオブジェクト一覧）を管理する Zustand store。
// サーバ権威のスナップショットと、自分が送ったがまだ ack されていない inFlightOps を分離保持する。

export interface CanvasMetaState {
  readonly title: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly thumbnailKey?: string | undefined;
  readonly thumbnailUrl?: string | undefined;
}

interface CanvasDocState {
  canvasId: string | null;
  serverSeq: number;
  meta: CanvasMetaState;
  /** 画面表示用（サーバ確定状態に inFlightOps を重ねたもの） */
  objects: Record<string, CanvasObject>;
  /** サーバ確定スナップショット */
  committedMeta: CanvasMetaState;
  committedObjects: Record<string, CanvasObject>;
  /** ローカルで先行適用した op（ack 待ち） */
  inFlightOps: CanvasOp[];

  loadSnapshot: (params: {
    canvasId: string;
    serverSeq: number;
    objects: CanvasObject[];
    meta?: CanvasMetaState | undefined;
  }) => void;
  updateLocalMeta: (meta: Partial<CanvasMetaState>) => void;
  applyOptimistic: (op: CanvasOp) => void;
  /** サーバから op.commit を受信したときに呼ぶ */
  onServerCommit: (op: CanvasOp, serverSeq: number) => void;
  /** サーバから op.reject を受信したときに呼ぶ（自分の op が拒否） */
  onServerReject: (clientOpId: string) => void;
  /** 接続切断などで完全リセットするとき */
  reset: () => void;
}

const DEFAULT_META: CanvasMetaState = {
  title: DEFAULT_CANVAS_TITLE,
  createdAt: "",
  updatedAt: "",
};

export const useCanvasDocStore = create<CanvasDocState>((set) => ({
  canvasId: null,
  serverSeq: 0,
  meta: DEFAULT_META,
  objects: {},
  committedMeta: DEFAULT_META,
  committedObjects: {},
  inFlightOps: [],

  loadSnapshot: ({ canvasId, serverSeq, objects, meta }) =>
    set((state) => {
      if (state.canvasId === canvasId && serverSeq < state.serverSeq) {
        // op.reject 後の再取得が古い snapshot を返した場合は、適用済み commit を巻き戻さない。
        return state;
      }
      const base = {
        meta: meta ?? (state.canvasId === canvasId ? state.meta : DEFAULT_META),
        objects: Object.fromEntries(objects.map((o) => [o.id, o])),
      };
      const inFlightOps = state.canvasId === canvasId ? state.inFlightOps : [];
      const next = inFlightOps.reduce(applyOpToState, base);
      return {
        canvasId,
        serverSeq,
        committedMeta: base.meta,
        committedObjects: base.objects,
        meta: next.meta,
        objects: next.objects,
        inFlightOps,
      };
    }),

  updateLocalMeta: (meta) =>
    set((state) => ({
      meta: {
        ...state.meta,
        ...meta,
      },
      committedMeta: {
        ...state.committedMeta,
        ...meta,
      },
    })),

  applyOptimistic: (op) =>
    set((state) => {
      const next = applyOpToState({ meta: state.meta, objects: state.objects }, op);
      return {
        inFlightOps: [...state.inFlightOps, op],
        meta: next.meta,
        objects: next.objects,
      };
    }),

  onServerCommit: (op, serverSeq) =>
    set((state) => {
      if (serverSeq <= state.serverSeq) {
        // snapshot でより新しい状態を反映済みの場合、遅延到着した commit で巻き戻さない。
        // ただし、自分の op の ack であれば inFlightOps からは取り除き、以後の再適用を止める。
        const remaining = state.inFlightOps.filter((p) => p.clientOpId !== op.clientOpId);
        if (remaining.length === state.inFlightOps.length) return state;
        const next = remaining.reduce(applyOpToState, {
          meta: state.committedMeta,
          objects: state.committedObjects,
        });
        return {
          meta: next.meta,
          objects: next.objects,
          inFlightOps: remaining,
        };
      }
      // 自分が送った op の ack（clientOpId 一致）なら inFlightOps から取り除き、
      // サーバ確定値に置き換える。他者の op はそのまま objects/meta に反映する。
      const remaining = state.inFlightOps.filter((p) => p.clientOpId !== op.clientOpId);
      const committed = applyOpToState(
        { meta: state.committedMeta, objects: state.committedObjects },
        op
      );
      const next = remaining.reduce(applyOpToState, committed);
      return {
        serverSeq,
        committedMeta: committed.meta,
        committedObjects: committed.objects,
        meta: next.meta,
        objects: next.objects,
        inFlightOps: remaining,
      };
    }),

  onServerReject: (clientOpId) =>
    set((state) => {
      const target = state.inFlightOps.find((p) => p.clientOpId === clientOpId);
      if (!target) return state;
      const remaining = state.inFlightOps.filter((p) => p.clientOpId !== clientOpId);
      // 拒否された op だけを除外し、サーバ確定スナップショットへ残りの楽観 op を再適用する。
      const next = remaining.reduce(applyOpToState, {
        meta: state.committedMeta,
        objects: state.committedObjects,
      });
      return {
        meta: next.meta,
        objects: next.objects,
        inFlightOps: remaining,
      };
    }),

  reset: () =>
    set({
      canvasId: null,
      serverSeq: 0,
      meta: DEFAULT_META,
      objects: {},
      committedMeta: DEFAULT_META,
      committedObjects: {},
      inFlightOps: [],
    }),
}));

function applyOpToState(
  state: { readonly meta: CanvasMetaState; readonly objects: Record<string, CanvasObject> },
  op: CanvasOp
): { readonly meta: CanvasMetaState; readonly objects: Record<string, CanvasObject> } {
  return {
    meta: applyOpToMeta(state.meta, op),
    objects: applyOpToObjects(state.objects, op),
  };
}

function applyOpToMeta(meta: CanvasMetaState, op: CanvasOp): CanvasMetaState {
  if (op.type !== "canvas.meta.update") return meta;
  return {
    ...meta,
    ...(op.title !== undefined ? { title: op.title, updatedAt: op.clientCreatedAt } : {}),
    ...(op.thumbnailKey !== undefined
      ? { thumbnailKey: op.thumbnailKey, updatedAt: op.clientCreatedAt }
      : {}),
  };
}

/**
 * 単一 op を objects 連想配列に適用する。
 * サーバ権威モデルでは、同じ関数を server / client の両方で適用することで決定論的に状態を再現する。
 */
function applyOpToObjects(
  objects: Record<string, CanvasObject>,
  op: CanvasOp
): Record<string, CanvasObject> {
  switch (op.type) {
    case "object.create":
      return { ...objects, [op.object.id]: op.object };
    case "object.delete": {
      const next = { ...objects };
      delete next[op.objectId];
      return next;
    }
    case "object.transform": {
      const current = objects[op.objectId];
      if (!current) return objects;
      return {
        ...objects,
        [op.objectId]: applyObjectTransform(current, op.transform),
      };
    }
    case "object.text.update": {
      const current = objects[op.objectId];
      if (!current) return objects;
      return {
        ...objects,
        [op.objectId]: { ...current, text: op.text } as CanvasObject,
      };
    }
    case "pen.stroke.commit":
      return { ...objects, [op.object.id]: op.object };
    case "object.attachment.update": {
      const current = objects[op.objectId];
      if (!current) return objects;
      // image / video / link-card に共通する src/imageUrl 系のフィールドを更新する。
      if (current.type === "image" || current.type === "video") {
        return {
          ...objects,
          [op.objectId]: { ...current, src: op.src } as CanvasObject,
        };
      }
      if (current.type === "link-card") {
        return {
          ...objects,
          [op.objectId]: { ...current, imageUrl: op.src } as CanvasObject,
        };
      }
      return objects;
    }
    case "canvas.meta.update":
      return objects;
    default:
      return objects;
  }
}

function applyObjectTransform(
  current: CanvasObject,
  transform: TransformObjectOp["transform"]
): CanvasObject {
  if (current.type !== "pen-stroke") {
    return { ...current, ...transform } as CanvasObject;
  }

  const nextX = transform.x ?? current.x;
  const nextY = transform.y ?? current.y;
  const dx = nextX - current.x;
  const dy = nextY - current.y;

  // ペンストロークの points は stage 座標で保持しているため、
  // オブジェクト移動時は外接矩形と同じ差分だけポイント群も移動する。
  return {
    ...current,
    ...transform,
    points:
      dx === 0 && dy === 0
        ? current.points
        : current.points.map((point) => ({ ...point, x: point.x + dx, y: point.y + dy })),
  };
}
